/**
 * 학원 신청 — 항목 리스트에서 채워나가는 방식 (운영홈 학원정보와 동일 패턴)
 *
 * 진입 시 본인의 draft 학원을 자동 생성 또는 이어쓰기.
 * 각 행 [입력]/[수정] → 풀스크린 편집 페이지로 이동, 저장하면 Firestore 자동 반영.
 * 필수 항목 모두 채우면 [승인 요청] 버튼 활성화.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';

const FIELD_NAME = { study: '교과 과정', sports: '운동 / 태권도', arts: '음악 / 미술' };
const TYPE_NAME = { academy: '학원', home: '교습소', personal: '개인레슨' };

export const APPLY_FIELDS = [
  { key: 'field', label: '분야', kind: 'select', required: true },
  { key: 'type', label: '운영 형태', kind: 'select', required: true },
  { key: 'name', label: '학원 이름', kind: 'text', required: true },
  { key: 'grades', label: '수업 대상', kind: 'lines', required: true },
  { key: 'location', label: '주소', kind: 'address', required: true },
  { key: 'phone', label: '연락처', kind: 'text', required: true },
  { key: 'hours', label: '영업시간', kind: 'lines', required: true },
  { key: 'shuttle', label: '셔틀버스', kind: 'toggle', required: true },
  { key: 'intro', label: '학원 소개', kind: 'textarea', required: true },
  { key: 'tuition', label: '학원비', kind: 'pairs', required: false },
  { key: 'hashtags', label: '해시태그', kind: 'chips', required: false },
  { key: 'links', label: '홈페이지 · SNS', kind: 'links', required: false },
  { key: 'videos', label: '수업영상', kind: 'videos', required: false },
  { key: 'promoPhotos', label: '사진', kind: 'photos', required: true },
];

const isEmpty = (f, a) => {
  const v = a[f.key];
  if (f.kind === 'photos') {
    // 사진 = 대표(photoUrl) + 홍보사진(promoPhotos) 합산
    return !a.photoUrl && (!a.promoPhotos || a.promoPhotos.length === 0);
  }
  if (
    f.kind === 'chips' || f.kind === 'lines' || f.kind === 'pairs'
    || f.kind === 'videos'
  ) {
    return !v || v.length === 0;
  }
  if (f.kind === 'toggle') {
    return v === null || v === undefined;
  }
  if (f.kind === 'links') {
    return !a.homepage && !a.instagram && !a.blog;
  }
  return !v || (typeof v === 'string' && !v.trim());
};

const displayValue = (f, a) => {
  if (isEmpty(f, a)) return '';
  const v = a[f.key];
  if (f.kind === 'select') {
    if (f.key === 'field') return FIELD_NAME[v] || v;
    if (f.key === 'type') return TYPE_NAME[v] || v;
    return v;
  }
  if (f.kind === 'toggle') return v ? '운행해요' : '운행 안 해요';
  if (f.kind === 'chips' || f.kind === 'lines') return v.join(' · ');
  if (f.kind === 'pairs') return v.map((t) => `${t.label} ${t.price}`).join(' · ');
  if (f.kind === 'photos') return `${(a.photoUrl ? 1 : 0) + (a.promoPhotos?.length || 0)}개`;
  if (f.kind === 'videos') return `${v.length}개`;
  if (f.kind === 'links') {
    return [a.homepage, a.instagram, a.blog].filter(Boolean).join(' · ');
  }
  return v;
};

const AcademyApplyPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { reloadAcademies } = useAcademy();
  const [academy, setAcademy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isDemo = userData?.provider === 'demo';

  const reload = useCallback(async () => {
    if (!userData?.uid) return;
    let draft = await academyService.getMyDraftAcademy(userData.uid);
    if (!draft) draft = await academyService.createDraftAcademy(userData.uid);
    setAcademy(draft);
    setLoading(false);
  }, [userData?.uid]);

  useEffect(() => {
    if (isDemo) {
      alert('체험 모드에서는 실제 학원을 신청할 수 없어요.\n둘러보기를 종료한 뒤 가입해서 진행해 주세요.');
      navigate(-1);
      return;
    }
    reload();
  }, [isDemo, navigate, reload]);

  const requiredFields = APPLY_FIELDS.filter((f) => f.required);
  const filledRequired = academy
    ? requiredFields.filter((f) => !isEmpty(f, academy)).length
    : 0;
  const totalRequired = requiredFields.length;
  const filledAll = filledRequired;
  const canSubmit = !!academy && filledAll === totalRequired;

  const onSubmit = async () => {
    if (!canSubmit || submitting || !academy) return;
    setSubmitting(true);
    try {
      await academyService.submitDraftAcademy(academy.id, userData.uid);
      await reloadAcademies();
      setDone(true);
    } catch (err) {
      console.error('승인 요청 실패:', err);
      alert('승인 요청에 실패했어요. 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !academy) return <BrandLoader />;

  if (done) {
    return (
      <Wrap>
        <DoneArea>
          <IoCheckmarkCircle size={66} color="#4F46E5" />
          <DoneTitle>학원 등록 완료!</DoneTitle>
          <DoneText>
            신청한 학원이 디렉토리에 공개됐어요.<br />
            이제 학원 운영 화면에서 관리를 시작할 수 있어요.
          </DoneText>
        </DoneArea>
        <Bottom>
          <SubmitBtn
            type="button"
            onClick={() => navigate('/app/biz/start', { replace: true })}
          >
            확인
          </SubmitBtn>
        </Bottom>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Content>
        <PageTitle>학원 정보 입력</PageTitle>
        <Intro>
          <IntroTitle>한 번에 다 안 적어도 괜찮아요</IntroTitle>
          <IntroDesc>
            입력하는 내용은 자동 저장돼요. 나중에 이어서 작성해도 됩니다.<br />
            필수 항목을 모두 채우면 승인 요청을 보낼 수 있어요.
          </IntroDesc>
        </Intro>

        <Progress>
          <ProgressLabel>입력 진행 {filledAll}/{totalRequired}</ProgressLabel>
          <ProgressBar>
            <ProgressFill style={{ width: `${(filledAll / totalRequired) * 100}%` }} />
          </ProgressBar>
        </Progress>

        <SecLabel>학원 정보</SecLabel>

        {APPLY_FIELDS.map((f) => {
          const empty = isEmpty(f, academy);
          const value = empty ? '미입력' : displayValue(f, academy);
          return (
            <FieldRow
              key={f.key}
              type="button"
              onClick={() => navigate(`/academy/apply/edit/${f.key}`)}
            >
              <FieldHeader>
                <FieldLabel>{f.label}</FieldLabel>
                {f.required && <RequiredStar>*</RequiredStar>}
              </FieldHeader>
              <FieldValueRow>
                <FieldValue $empty={empty}>{value}</FieldValue>
                <EditTag>{empty ? '입력' : '수정'}</EditTag>
              </FieldValueRow>
            </FieldRow>
          );
        })}
      </Content>

      <Bottom>
        <SubmitHint>
          {canSubmit
            ? '모든 필수 항목이 채워졌어요. 승인 요청을 보낼 수 있어요.'
            : `필수 ${totalRequired - filledAll}개 남았어요.`}
        </SubmitHint>
        <SubmitBtn
          type="button"
          disabled={!canSubmit || submitting}
          onClick={onSubmit}
        >
          {submitting ? '요청 중...' : '승인 요청 보내기'}
        </SubmitBtn>
      </Bottom>
    </Wrap>
  );
};

export default AcademyApplyPage;

/* ── styled ─────────────────────────────────────── */
const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

const Content = styled.div`
  flex: 1;
  padding: 24px 18px;
`;

const PageTitle = styled.p`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Intro = styled.div`
  padding: 16px 18px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.primaryLight};
  margin-top: 16px;
`;

const IntroTitle = styled.p`
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const IntroDesc = styled.p`
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 6px;
`;

const Progress = styled.div`
  margin: 18px 0 4px;
`;

const ProgressLabel = styled.p`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 6px;
`;

const ProgressBar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #ECEDF1;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.25s ease;
`;

const SecLabel = styled.p`
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 22px 0 10px;
`;

const FieldRow = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 6px;
  padding: 14px 4px;
  border-bottom: 1px solid #F0F1F4;
  background: none;
  text-align: left;
`;

const FieldHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RequiredStar = styled.span`
  color: #DC2626;
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  margin-left: 2px;
`;

const FieldValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FieldValue = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $empty, theme }) => ($empty ? theme.colors.text.hint : theme.colors.text.primary)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditTag = styled.span`
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 9px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const Bottom = styled.div`
  position: sticky;
  bottom: 0;
  padding: 10px 16px 22px;
  background: #fff;
  border-top: 1px solid #F0F1F4;
`;

const SubmitHint = styled.p`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 8px;
  text-align: center;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;

const DoneArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 30px;
`;

const DoneTitle = styled.p`
  font-size: 22px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 18px 0 10px;
`;

const DoneText = styled.p`
  font-size: 14px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
